import { useCallback, useEffect, useMemo, useState } from 'react';
import ReactTableWrapper from '../../components/ReactTableWrapper';
import ListPageLayout from '../../components/ListPageLayout';
import Sidebar from '../../Sidebar';
import { useCookies } from 'react-cookie';
import { displayError } from '../../helpers';
import { exportAsExcel } from '../../utils/ExcelExporter';
import { getUserAllowedPlants } from '../../services/PlantServices';
import { getMaintenanceReport } from '../../services/MaintenanceServices';
import {
    getDateRangeFromSalesSettings,
    getSalesSettingsSummary,
    loadSalesListSettings,
    resolvePlantFilter,
    saveSalesListSettings,
} from '../../utils/salesListSettings';
import SalesListSettingsModal from '../../components/SalesListSettingsModal';

function MaintenanceReport() {
    const [data, setData] = useState({ records: [] });
    const [cookies] = useCookies(['myToken', 'myUserId']);
    const [isLoading, setIsLoading] = useState(true);
    const [plants, setPlants] = useState([]);
    const [isInitialized, setIsInitialized] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [salesSettings, setSalesSettings] = useState(() => loadSalesListSettings(cookies.myUserId));

    const activeDateRange = useMemo(
        () => getDateRangeFromSalesSettings(salesSettings),
        [salesSettings],
    );

    useEffect(() => {
        getUserAllowedPlants(cookies).then((response) => {
            setPlants((response.plant_list || [])
                .filter((p) => p.status === true || p.status === 1 || p.status === 'Active')
                .map((p) => ({ value: p.id, label: p.plant_alias })));
            setIsInitialized(true);
        });
    }, [cookies]);

    const settingsSummary = useMemo(
        () => getSalesSettingsSummary(salesSettings, plants, []),
        [salesSettings, plants],
    );

    const fetchRecords = useCallback(() => {
        const plantId = resolvePlantFilter(salesSettings, plants);
        const [rangeFrom, rangeTo] = activeDateRange;
        if (!plantId || !rangeFrom?.isValid() || !rangeTo?.isValid()) {
            setIsLoading(false);
            return Promise.resolve();
        }
        setIsLoading(true);
        return getMaintenanceReport(cookies, {
            from_date: rangeFrom.format('DD-MM-YYYY'),
            upto_date: rangeTo.format('DD-MM-YYYY'),
            plant_id: plantId,
        })
            .then((response) => {
                setData({
                    records: (response.maintenance_report_list || []).map((row) => ({
                        ...row,
                        hide_row_edit: 'Yes',
                        hide_row_delete: 'Yes',
                        hide_row_print: 'Yes',
                    })),
                });
            })
            .catch((error) => {
                displayError(error?.response?.data || { message: 'Failed to load maintenance report.' }, 'Load Failed');
                setData({ records: [] });
            })
            .finally(() => setIsLoading(false));
    }, [activeDateRange, cookies, plants, salesSettings]);

    useEffect(() => {
        if (isInitialized) fetchRecords();
    }, [fetchRecords, isInitialized]);

    const columns = useMemo(() => ([
        { fieldName: 'log_date', headerName: 'Date' },
        { fieldName: 'plant_alias', headerName: 'Plant' },
        { fieldName: 'production_qty', headerName: 'Production m3', align: 'right' },
        { fieldName: 'eb_units_kwh', headerName: 'EB kWh', align: 'right' },
        { fieldName: 'pump_hours', headerName: 'Pump Hrs', align: 'right' },
        { fieldName: 'diesel_litres', headerName: 'Diesel L', align: 'right' },
        { fieldName: 'total_cost', headerName: 'Total Cost', align: 'right' },
        { fieldName: 'cost_per_m3', headerName: 'Cost / m3', align: 'right' },
        { fieldName: 'hours_per_litre', headerName: 'Hrs / Litre', align: 'right' },
        { fieldName: 'kwh_per_m3', headerName: 'kWh / m3', align: 'right' },
    ]), []);

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <div id="page-wrap">
                <ListPageLayout
                    moduleLabel="MAINTENANCE"
                    title="Maintenance Report"
                    subtitle={`Day-wise KPIs — cost/m3, hrs/litre, kWh/m3. ${settingsSummary}`}
                    onOpenSettings={() => setShowSettings(true)}
                    onRefresh={fetchRecords}
                    onExportCSV={() => exportAsExcel(columns, data.records, 'maintenance_report')}
                    isLoading={isLoading}
                >
                    <ReactTableWrapper
                        data={data.records}
                        columns={columns}
                        isLoading={isLoading}
                    />
                </ListPageLayout>
                <SalesListSettingsModal
                    show={showSettings}
                    onClose={() => setShowSettings(false)}
                    settings={salesSettings}
                    plants={plants}
                    onSave={(nextSettings) => {
                        saveSalesListSettings(cookies.myUserId, nextSettings);
                        setSalesSettings(nextSettings);
                        setShowSettings(false);
                    }}
                />
            </div>
        </div>
    );
}

export default MaintenanceReport;
